import { Component, ElementRef, inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ZzzConfigService } from 'src/app/zzz/core/config/config.service';
import { AbstractControl, FormGroup, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Feature, GeometryFormFieldInputComponent, IgoMap } from '@igo2/geo';
import { MapState } from '@igo2/integration';
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, map, Observable, switchMap, take } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { CommentairePlansOfficielsDto, InscrireCommentaireService } from './sigt-inscrire-commentaire.service';
import { Constantes } from 'src/app/shared/constantes';
import { ActivatedRoute } from '@angular/router';
import { ToolComponent } from '@igo2/common/tool';
import { MessageService } from '@igo2/core/message';
import { LanguageService } from '@igo2/core/language';
import { TranslateModule } from '@ngx-translate/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import * as olStyle from 'ol/style';
import Point from 'ol/geom/Point';

import { Style, Icon } from 'ol/style';
import { Feature as OlFeature } from 'ol';
import type { Type } from 'ol/geom/Geometry';
import type { default as OlGeometry } from 'ol/geom/Geometry';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { ConsultationsPubliqueService } from 'src/app/shared/services/pilotage-consultation-publique.service';
import { ConsultationPublique } from 'src/app/shared/models/consultation-publique.models';
import { LineString } from 'ol/geom';
import { ValeurDomaineDto } from 'src/app/shared/models/domaines.dto';
import { DomainesService } from 'src/app/shared/services/domaines.service';

@ToolComponent({
  name: 'sigt-inscrire-commentaire',
  title: 'Inscrire un commentaire',
  icon: 'comment-edit-outline'
})
@Component({
    selector: 'app-sigt-inscrire-commentaire',
    templateUrl: './sigt-inscrire-commentaire.component.html',
    styleUrls: ['./sigt-inscrire-commentaire.component.scss'],
    providers: [InscrireCommentaireService],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatButtonModule,
        GeometryFormFieldInputComponent
    ]
})
export class SigtInscrireCommentaireComponent implements OnInit {
  form: UntypedFormGroup;
  messageApplication: { [key: string]: string } = {};
  captcha: BehaviorSubject<string> = new BehaviorSubject("");
  geometryType: Type = 'Point';
  radius: number = 1000;
  drawStyle$: BehaviorSubject<olStyle.Style | ((feature, resolution) => olStyle.Style)> = new BehaviorSubject(undefined);
  consultationsPublique: ConsultationPublique[] = [];
  domaineRegions: ValeurDomaineDto[] = [];
  @ViewChild('commentaire') commentaireElement: ElementRef;
  activeMessagesToastId: number[] = [];


  get map(): IgoMap {
    return this.mapState.map;
  };

  private readonly okEntryPointStyle = (
    _feature: OlFeature<OlGeometry>,
    _resolution: number
  ): Style => {
    return new olStyle.Style({
      image: new olStyle.Circle({
        radius: 5,
        stroke: new olStyle.Stroke({
          width: 2,
          color: 'rgba(0, 153, 255)'
        }),
        fill: new olStyle.Fill({
          color: 'rgba(0, 153, 255, 0.2)'
        })
      })
    });
  };

  typePlan = 'PRDTP';

  // TerrAPI du MSI, par la route /terrapi de la passerelle SIGT (l'adresse
  // geoegl.msp.gouv.qc.ca/apis/terrapi cesse de fonctionner le 2027-01-27).
  readonly #urlTerrApi = inject(ZzzConfigService).get().authApiEndPoint + '/terrapi';

  constructor(
    private formBuilder: UntypedFormBuilder,
    private mapState: MapState,
    private httpClient: HttpClient,
    private messageService: MessageService,
    private inscrireCommentaireService: InscrireCommentaireService,
    private languageService: LanguageService,
    private route: ActivatedRoute,
    private consultationsPubliqueService: ConsultationsPubliqueService,
    private domaineService: DomainesService
  ) { }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      nom: ["", [Validators.required, Validators.pattern("[a-zA-Z'éèê ]+")]],
      prenom: ["", [Validators.required, Validators.pattern("[a-zA-Z'éèê ]+")]],
      courriel: ["", [Validators.required, Validators.email]],
      commentaire: ["", [
        Validators.required,
        Validators.minLength(10),
        this.validerPattern]],
      positionSurCarte: ["", [Validators.required]],
      positionSurCarteForViewOnly: ["", [Validators.required]],
      codeRegionAdministrative: ["", [Validators.required]],
      textCaptcha: ["", [Validators.required]]
    }, { validators: [this.validerChamps.bind(this)] });

    this.messageApplication.informationsEnregistreesSucces = Constantes.MessageApplicatif.ElementEnregistrer_7I;
    this.messageApplication.echecOperation = Constantes.MessageApplicatif.OperationNonComplete_11E;
    this.messageApplication.valeurObligatoire = Constantes.MessageApplicatif.ValeurObligatoire_1E;
    this.messageApplication.valeurInvalid = Constantes.MessageApplicatif.Invalide_2E;
    this.messageApplication.minCommentaire = Constantes.MessageApplicatif.CommentaireAuMoins10_20E;
    this.messageApplication.invalidePointSurCarte = Constantes.MessageApplicatif.Information_22E
      .replace("{0}", this.languageService.translate
        .instant('sigt.integration.commenterPlanOfficiel.form.messagesPlaceholders.invalidePointSurCarte'));
    this.messageApplication.invalideRegionSurCarte = this.languageService.translate
        .instant('sigt.integration.commenterPlanOfficiel.form.messagesPlaceholders.invalideRegionSurCarte');

    this.consultationsPubliqueService.getActives().subscribe({
      next: result => {
        console.log("getActives() dans sigt-inscrire-commentaire.component.ts");
        this.consultationsPublique = result.valeur;
      },
      error: err => console.error('GET failed', err)
    });

    this.domaineService.obtenirValeurs('REGION_ADMIN').subscribe(
      resp => this.domaineRegions = resp.valeursDomaine
    );

    this.form.controls.positionSurCarte.valueChanges.pipe(
      filter(positionSurCarte => !!positionSurCarte && Array.isArray(positionSurCarte.coordinates) && positionSurCarte.coordinates.length === 2),
      switchMap(positionSurCarte =>
        this.rechercherRegionPourCoordonnee(positionSurCarte.coordinates).pipe(
          map(result => ({ positionSurCarte, result }))
        )
      )
    ).subscribe(({ positionSurCarte, result }) => {
      this.handleRechercheRegionResult(positionSurCarte, result);
      this.commentaireElement.nativeElement.focus(); //pour activer le bouton soumettre
    });

    this.drawStyle$.next(this.okEntryPointStyle);
    this.obtenirCode();
    this.route.queryParams
      .pipe(
        debounceTime(500),
        take(1)
      )
      .subscribe((params) => {
        if (params.visiblelayers) {
          const tester = new RegExp(/plansaffectation/gi);
          if (tester.test(params.visiblelayers)) {
            this.typePlan = 'PATP';
          }
        }
      });
  }

  private rechercherRegionPourCoordonnee(coords: [number, number]): Observable<{ features: any[] }> {
    const coordString = `${coords[0]},${coords[1]}`;
    const url = `${this.#urlTerrApi}/locate?type=regadmin&loc=${coordString}`;
    return this.httpClient.get<{ features: any[] }>(url);
  }

  private handleRechercheRegionResult(positionSurCarte: any, result: any)
  {
    const regionCode = result.features[0]?.properties?.code;
    if (!regionCode) {
        let toastMsg = this.messageService.error(this.messageApplication.invalidePointSurCarte);
        this.activeMessagesToastId.push(toastMsg.toastId);
        this.viderChampsPosition();
        return;
    }

    if (this.regionExisteDansUneConsultation(result.features[0].properties.code)) {
      this.form.patchValue({
        codeRegionAdministrative: result.features[0].properties.code,
        positionSurCarteForViewOnly: `${positionSurCarte.coordinates[0]}, ${positionSurCarte.coordinates[1]}`
      });
      this.activeMessagesToastId.forEach(id => this.messageService.remove(id));
      this.activeMessagesToastId = [];
    } else {
      const regions = this.consultationsPublique
        .flatMap(c => c.regions)
        .map(noRegion => this.domaineRegions.find(r => r.valeur  === noRegion)?.description);
      var message =  this.messageApplication.invalideRegionSurCarte +
        "<ul><li>" + regions.join("</li><li>") + "</li></ul>";
      let toastMsg = this.messageService.error(message);
      this.activeMessagesToastId.push(toastMsg.toastId);
      this.viderChampsPosition();
    }
  }

  /** Renvoie true si le regionCode est présent dans au moins une consultation */
  private regionExisteDansUneConsultation(regionCode: string): boolean {
    return this.consultationsPublique.some(c => c.regions?.some(r => r === regionCode));
  }

  /** Efface simplement les champs liés à la position */
  private viderChampsPosition(): void {
    this.form.patchValue({
      positionSurCarte: '',
      codeRegionAdministrative: '',
      positionSurCarteForViewOnly: ''
    });
  }

  valeurInvalide(formName: string): string {
    if (formName === "courriel") {
      return this.messageApplication.valeurInvalid.replace('{0}', this.languageService.translate
        .instant('sigt.integration.commenterPlanOfficiel.form.courriel'));
    } else if (formName === "nom") {
      return this.messageApplication.valeurInvalid.replace('{0}', this.languageService.translate
        .instant('sigt.integration.commenterPlanOfficiel.form.nom'));
    } else if (formName === "prenom") {
      return this.messageApplication.valeurInvalid.replace('{0}', this.languageService.translate
        .instant('sigt.integration.commenterPlanOfficiel.form.prenom'));
    } else if (formName === "commentaire") {
      return this.messageApplication.valeurInvalid.replace('{0}', this.languageService.translate
        .instant('sigt.integration.commenterPlanOfficiel.form.commentaire.nom'));
    }
  }

  valeurObligatoire(formName: string): string {
    if (formName === "nom") {
      return this.messageApplication.valeurObligatoire.replace('{0}', this.languageService.translate
        .instant('sigt.integration.commenterPlanOfficiel.form.nom'));
    } else if (formName === "prenom") {
      return this.messageApplication.valeurObligatoire.replace('{0}', this.languageService.translate
        .instant('sigt.integration.commenterPlanOfficiel.form.prenom'));
    } else if (formName === "courriel") {
      return this.messageApplication.valeurObligatoire.replace('{0}', this.languageService.translate
        .instant('sigt.integration.commenterPlanOfficiel.form.courriel'));
    } else if (formName === "commentaire") {
      return this.messageApplication.valeurObligatoire.replace('{0}', this.languageService.translate
        .instant('sigt.integration.commenterPlanOfficiel.form.commentaire.nom'));
    } else if (formName === "captcha") {
      return this.messageApplication.valeurObligatoire.replace('{0}', this.languageService.translate
        .instant('sigt.integration.commenterPlanOfficiel.form.codeCaptcha'));
    }
  }

  validerPattern(ctrl: AbstractControl): ValidationErrors | null {
    if (ctrl.value?.length >= 10 //pour éviter de montrer une erreur avant de saisir le minimum des caracteres
      && ctrl.value.match(Constantes.regexValidationCommentaire) === null) {
      return {
        pattern: true
      };
    }
    return null;
  }

  obtenirCode(callback?: () => void): void {
    this.inscrireCommentaireService.imagecaptcha().subscribe({
      next: (blob: Blob) => {
        const reader = new FileReader();

        reader.onload = () => {
          this.captcha.next(reader.result as string);

          if (this.form) {
            this.form.patchValue({
              textCaptcha: ''
            }, { emitEvent: false });
            this.form.get('positionSurCarteForViewOnly').setErrors(null, { emitEvent: false });
            this.form.get('commentaire').setErrors(null, { emitEvent: false });
            this.form.get('prenom').setErrors(null, { emitEvent: false });
            this.form.get('nom').setErrors(null, { emitEvent: false });
            this.form.get('courriel').setErrors(null, { emitEvent: false });
            this.form.get('textCaptcha').setErrors(null, { emitEvent: false });
          }

          if (callback) {
            callback();
          }
        };

        reader.readAsDataURL(blob);
      }
    });
  }

  soumettre(): void {
    if (this.form.valid) {
      var formValeur = this.form.value;

      var consultation = this.getConsultationFromRegion(formValeur.codeRegionAdministrative);

      var commentairePlanOfficiel: CommentairePlansOfficielsDto = {
        commentaire: formValeur.commentaire,
        geometrieX: formValeur.positionSurCarte.coordinates[0],
        geometrieY: formValeur.positionSurCarte.coordinates[1],
        noRegionAdmin: formValeur.codeRegionAdministrative,
        consultationPubliqueId: consultation.id,
        typePlan: this.typePlan,
        captcha: formValeur.textCaptcha,
        nom: formValeur.nom,
        prenom: formValeur.prenom,
        courriel: formValeur.courriel
      };

      this.inscrireCommentaireService.creerCommentairePlanOfficiel(commentairePlanOfficiel).then(
        response => {
          console.log(response);
          if (response === undefined) {
            this.obtenirCode(() => {
              this.form.get('textCaptcha').setErrors({ captcha: 'invalid' }, { emitEvent: true });
            });
            this.messageService.error(this.messageApplication.echecOperation);
            return;
          }
          this.messageService.info(this.messageApplication.informationsEnregistreesSucces);
          this.initialiserChamps();
          this.obtenirCode();
        },
        error => {
          this.obtenirCode(() => {
            if (error.status === 400 && error.error.includes('Captcha Invalide')) {
              this.form.get('textCaptcha').setErrors({ captcha: 'invalid' }, { emitEvent: true });
            }
          });

          this.messageService.error(this.messageApplication.echecOperation);
        }
      );
    }
  }

  getConsultationFromRegion(codeRegion: string) : ConsultationPublique {
    //Une regle d'affaire stipule qu'il ne peut y avoir deux consultations publiques pour une même region dans le même lapse de temps.
    return this.consultationsPublique.find(consultation => consultation.regions.some(region => region === codeRegion))!;
  }

  initialiserChamps() {
    this.form.patchValue({
      positionSurCarteForViewOnly: '',
      commentaire: '',
      positionSurCarte: '',
      codeRegionAdministrative: '',
      nom: '',
      prenom: '',
      courriel: '',
      textCaptcha: ''
    });
    this.form.get('positionSurCarteForViewOnly').setErrors(null, { emitEvent: false });
    this.form.get('commentaire').setErrors(null, { emitEvent: false });
    this.form.get('prenom').setErrors(null, { emitEvent: false });
    this.form.get('nom').setErrors(null, { emitEvent: false });
    this.form.get('courriel').setErrors(null, { emitEvent: false });
    this.form.get('textCaptcha').setErrors(null, { emitEvent: false });
  }

  initialiserPositionCarte() {
    this.form.patchValue({
      positionSurCarte: '',
      positionSurCarteForViewOnly: ''
    });
  }

  validerChamps(control: FormGroup){
    const positionSurCarteForViewOnly = control.controls["positionSurCarteForViewOnly"].value;
    const commentaire = control.controls["commentaire"].value;
    const prenom = control.controls["prenom"].value;
    const nom = control.controls["nom"].value;
    const courriel = control.controls["courriel"].value;
    const textCaptcha = control.controls["textCaptcha"].value;
    if ((positionSurCarteForViewOnly === null || positionSurCarteForViewOnly === '') || (commentaire === null || commentaire === '') ||
    (prenom === null || prenom === '') || (nom === null || nom === '') || (courriel === null || courriel === '') ||
    (textCaptcha === null || textCaptcha === '')){
      return { notValid: true };
    }
  }

  estRequis(controlName: string): boolean {
    const control = this.form.get(controlName);
    if (!control || !control.validator) return false;
    const result = control.validator({} as AbstractControl);
    return !!result?.['required'];
  }

}

